import React, { useEffect, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import ApiTokenService from "../../services/v2/management/apitoken-service";
import ApiTokenAdd from "./ApiTokenAdd";
import PermissionsKo from "../shared/PermissionsKo";

const formatToLocalTime = (date) => {
  const timestamp = new Date(date);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return timestamp.toLocaleString(undefined, {
    timeZone: userTimeZone,
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });
};

const ApiTokensTable = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalToken, setModalToken] = useState(null); // Token seleccionado para eliminar
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null); // Error al eliminar
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const fetchTokens = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiTokenService.list(); // Llamada al servicio de tokens
      setTokens(response.data?.tokens || []);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setIsUnauthorized(true);
      }
      setError("Failed to load tokens.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  const getStatusBadge = (expiresAt) => {
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
    const isExpired = expirationDate < now;
    const isExpiringSoon = expirationDate - now < oneWeekInMs && !isExpired;

    if (isExpired) {
      return (
        <span className="px-2 py-1 bg-red-500 text-white rounded-full text-xs">
          Expired
        </span>
      );
    }

    if (isExpiringSoon) {
      return (
        <span className="px-2 py-1 bg-yellow-500 text-white rounded-full text-xs">
          Expiring Soon
        </span>
      );
    }

    return (
      <span className="px-2 py-1 bg-green-500 text-white rounded-full text-xs">
        Active
      </span>
    );
  };

  const deleteToken = async () => {
    if (!modalToken) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await ApiTokenService.deleteById(modalToken.id); // Llamada al servicio para eliminar
      setModalToken(null);
      fetchTokens(); // Actualizar la lista de tokens
    } catch (err) {
      setDeleteError("Failed to delete the token. Please try again.");
      console.error("Failed to delete token:", err);
    } finally {
      setDeleting(false);
    }
  };

  const closeModal = () => {
    setModalToken(null);
    setDeleteError(null); // Limpiar errores al cerrar el modal
  };
  const [showAddTokenModal, setShowAddTokenModal] = useState(false);

  if (isUnauthorized) {
    return <PermissionsKo />;
  }
  return (
    <>
      <div className="flex justify-between items-center mt-4">
        <ApiTokenAdd
          isOpen={showAddTokenModal}
          onClose={() => setShowAddTokenModal(false)}
          onSuccess={fetchTokens} // Actualiza los tokens al crear uno nuevo
        />
        <button
          className="flex items-center gap-2 px-2 py-1 bg-[#2B6CB0] text-white rounded-lg text-sm "
          onClick={() => setShowAddTokenModal(true)} // Abre el modal
        >
          <FaPlus />
          Add Token
        </button>
        <span className="text-sm text-gray-700 dark:text-gray-300">
          Available tokens remaining:{" "}
          {3 -
            tokens.filter((token) => new Date(token.expiresAt) > new Date())
              .length}
        </span>
      </div>

      <div className="max-h-full overflow-auto bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-md mt-4">
        <table className="w-full">
          <thead
            className="sticky top-0 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide rounded-t-lg"
            style={{ height: "3vh" }}
          >
            <tr>
              {[
                "Description",
                "Token",
                "Expires At",
                "Roles",
                "Status",
                "Actions",
              ].map((header) => (
                <th
                  key={header}
                  className="px-2 py-2 border-b border-gray-300 text-left font-semibold"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-10">
                  <div className="flex flex-col items-center text-gray-500 dark:text-gray-400">
                    <svg
                      className="animate-spin h-8 w-8 text-gray-500 dark:text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      ></path>
                    </svg>
                    <p className="mt-3 text-lg">Loading data, please wait...</p>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="6" className="text-center py-10">
                  <div className="flex flex-col items-center text-red-600">
                    <p className="mt-3 text-lg font-semibold">
                      Something went wrong!
                    </p>
                    <p className="text-sm">
                      Please reload the page to try again.
                    </p>
                  </div>
                </td>
              </tr>
            ) : tokens.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-10">
                  <div className="flex flex-col items-center text-gray-500 dark:text-gray-400">
                    <p className="mt-3 text-lg">No tokens to display.</p>
                  </div>
                </td>
              </tr>
            ) : (
              tokens.map((token, index) => {
                const expirationDate = new Date(token.expiresAt);
                const isExpired = expirationDate < new Date();

                return (
                  <tr
                    key={token.id}
                    className={`${
                      index % 2 === 0
                        ? "bg-white dark:bg-gray-800"
                        : "bg-gray-50 dark:bg-primary-dark"
                    } hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200`}
                  >
                    <td
                      className={`px-2 py-2 text-gray-700 dark:text-gray-300 text-sm ${
                        isExpired ? "line-through" : ""
                      }`}
                    >
                      {token.description}
                    </td>
                    <td
                      className={`px-2 py-2 text-gray-700 dark:text-gray-300 text-sm truncate max-w-[150px] ${
                        isExpired ? "line-through" : ""
                      }`}
                    >
                      <span>
                        <span>{token.token.slice(0, 5)}</span>
                        <span className="blur-sm hover:blur-none">
                          {"********************"}
                        </span>
                      </span>
                    </td>

                    <td
                      className={`px-2 py-2 text-gray-700 dark:text-gray-300 text-sm ${
                        isExpired ? "line-through" : ""
                      }`}
                    >
                      {formatToLocalTime(token.expiresAt)}
                    </td>
                    <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm flex flex-wrap gap-2">
                      {token.roles.map((role, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-600 text-white rounded-full text-xs"
                        >
                          {role}
                        </span>
                      ))}
                    </td>
                    <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                      {getStatusBadge(token.expiresAt)}
                    </td>
                    <td className="px-2 py-2 text-gray-700 dark:text-gray-300 text-sm">
                      <button
                        onClick={() => setModalToken(token)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete Token"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {modalToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
              Confirm Deletion
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the token with description "
              <span className="font-semibold">{modalToken.description}</span>"?
              This action cannot be undone.
            </p>
            {deleteError && (
              <p className="text-red-500 text-sm mb-4">{deleteError}</p>
            )}
            <div className="flex justify-end gap-4">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={deleteToken}
                disabled={deleting}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApiTokensTable;
